import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  Loader2,
  Minus,
  Package,
  PlayCircle,
  Plus,
  Share2,
  ShoppingCart,
  Tags,
  Home,
  Sparkles,
  Building2,
  ShieldCheck,
  Truck,
} from 'lucide-react'
import { ApiError, api } from '@/lib/api'
import { dayShortLabel, formatPrice, localizedName, slugify, stockLabel } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { usePageTitle } from '@/lib/usePageTitle'
import { Markdown } from '@/components/Markdown'
import { RelatedProducts } from '@/components/RelatedProducts'
import { SimilarFragrances } from '@/components/SimilarFragrances'
import { ScentEvolutionPlayer } from '@/components/ScentEvolutionPlayer'
import { ReviewsSection } from '@/components/ReviewsSection'
import { FopRequisitesModal } from '@/components/FopRequisitesModal'
import { trackViewItem, trackAddToCart } from '@/lib/analytics'
import type { ProductDetail, ProductOptionVariant, WishlistItem } from '@/shared/types'

interface PublicSettings {
  price_in_usd: boolean
  shop_name: string
  home_page_enabled?: boolean
  variants_stepper?: boolean
}

type InfoTab = 'description' | 'attributes'

function buildDefaultSelection(product: ProductDetail): Record<number, number> {
  const next: Record<number, number> = {}
  for (const option of product.options) {
    const variant = option.variants.find((item) => item.is_default) ?? option.variants[0]
    if (variant) next[option.id] = variant.id
  }
  return next
}

function calcPrice(product: ProductDetail, selected: Record<number, number>) {
  let totalUsd = product.price_usd
  for (const option of product.options) {
    const variant = option.variants.find((item) => item.id === selected[option.id])
    if (variant) totalUsd += variant.price_usd_diff
  }
  const ratio = product.price_usd > 0 ? product.price_uah / product.price_usd : 0
  const totalUah = product.price_usd > 0 ? Math.round(totalUsd * ratio) : product.price_uah
  return { totalUsd, totalUah }
}

export function ProductPage() {
  const { productSlug } = useParams({ strict: false }) as { productSlug?: string }
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const T = useT('product')
  const tNav = useT('nav')
  const tCheckout = useT('checkout')
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const isMayParfum = (settings?.shop_name ?? '').toLowerCase().replace(/\s+/g, '').includes('mayparfum')

  const { data: wishlist } = useQuery<WishlistItem[]>({
    queryKey: ['wishlist'],
    queryFn: () => api.get<WishlistItem[]>('/wishlist'),
  })

  const { data: product, isLoading, isError } = useQuery<ProductDetail>({
    queryKey: ['product', productSlug],
    queryFn: () => api.get<ProductDetail>(`/products/${productSlug}`),
  })

  usePageTitle(product ? localizedName(lang, product.name_uk, product.name_en) : 'Product')

  const [selected, setSelected] = useState<Record<number, number>>({})
  const [quantity, setQuantity] = useState(1)
  const [variantQuantities, setVariantQuantities] = useState<Record<number, number>>({})
  const [photoIdx, setPhotoIdx] = useState(0)
  const [infoTab, setInfoTab] = useState<InfoTab>('description')
  const [showFopModal, setShowFopModal] = useState(false)

  useEffect(() => {
    if (!product) return
    setSelected(buildDefaultSelection(product)) // eslint-disable-line react-hooks/set-state-in-effect
    setQuantity(1)
    setVariantQuantities({})
    setPhotoIdx(0)
    trackViewItem({
      id: product.id,
      name: localizedName(lang, product.name_uk, product.name_en),
      price: product.price_uah,
    })
  }, [product, lang])



  const isMultiQtyProduct = useMemo(() => {
    if (!product || product.is_kit || product.options.length !== 1) return false
    const isStepperEnabled = Boolean(settings?.variants_stepper)
    const optionName = localizedName(lang, product.options[0].name_uk, product.options[0].name_en).toLowerCase()
    const isVolumeOption =
      optionName.includes('об') ||
      optionName.includes('vol') ||
      optionName.includes('ml') ||
      optionName.includes('мл')
    return isStepperEnabled && isVolumeOption
  }, [product, settings, lang])

  const variantRatio = product && product.price_usd > 0 ? product.price_uah / product.price_usd : 0

  const addMutation = useMutation<{ new_qty: number }, ApiError>({
    mutationFn: async () => {
      if (!product) throw new Error('Product not loaded')
      if (isMultiQtyProduct) {
        const itemsToAdd = Object.entries(variantQuantities).filter(([_, qty]) => qty > 0)
        let lastQty = 0
        for (const [variantId, qty] of itemsToAdd) {
          const res = await api.post<{ new_qty: number }>('/cart/add', {
            product_id: product.id,
            qty: qty,
            variants: { [product.options[0].id]: Number(variantId) },
          })
          lastQty = res.new_qty
        }
        return { new_qty: lastQty }
      } else {
        return api.post<{ new_qty: number }>('/cart/add', {
          product_id: product.id,
          qty: quantity,
          variants: selected,
        })
      }
    },
    onSuccess: async () => {
      haptic('success')
      if (product) {
        trackAddToCart({
          id: product.id,
          name: localizedName(lang, product.name_uk, product.name_en),
          price: product.price_uah,
          quantity: quantity,
        })
      }
      const cart = await api.get<{ count: number }>('/cart')
      setCartCount(cart.count)
      qc.invalidateQueries({ queryKey: ['cart'] })
      if (isMultiQtyProduct) {
        setVariantQuantities({})
      }
    },
    onError: (err) => {
      haptic('error')
      if (err instanceof ApiError && err.status === 401) {
        void navigate({ to: '/login' })
        return
      }
      alert(T.addFail)
    },
  })

  const wishlistMutation = useMutation<{ added: boolean }, ApiError>({
    mutationFn: () => {
      if (!product) throw new Error('Product not loaded')
      return api.post<{ added: boolean }>(`/wishlist/${product.id}`, {})
    },
    onSuccess: () => {
      haptic('light')
      qc.invalidateQueries({ queryKey: ['wishlist'] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    )
  }

  if (isError || !product) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center">
        <Package className="h-12 w-12 text-gray-300" />
        <h2 className="mt-3 text-lg font-semibold">{T.notFound}</h2>
        <Link to="/catalog" className="mt-4 rounded-full bg-brand-600 px-5 py-2 text-sm font-medium text-white">
          {T.back}
        </Link>
      </div>
    )
  }

  const name = localizedName(lang, product.name_uk, product.name_en)
  const description = localizedName(lang, product.description_uk, product.description_en)
  const specs = product.specs ?? []
  const hasAttributes = specs.length > 0
  const activeInfoTab: InfoTab = infoTab === 'attributes' && !hasAttributes ? 'description' : infoTab

  const brandSpec = specs.find(
    (s) => s.code === 'brand' || s.name_uk?.toLowerCase() === 'бренд' || s.name_en?.toLowerCase() === 'brand'
  )
  const brandName = brandSpec
    ? (localizedName(lang, brandSpec.value_uk, brandSpec.value_en) || brandSpec.value_uk).split(',')[0].trim()
    : null
  const brandSlug = brandSpec?.value_uk ? slugify(brandSpec.value_uk.split(',')[0].trim()) : null

  const total = !isMultiQtyProduct ? calcPrice(product, selected) : null
  const wished = (wishlist ?? []).some((item) => item.product_id === product.id)
  const homePageEnabled = settings?.home_page_enabled ?? true
  const daysLabel = dayShortLabel(lang)

  let totalQty = 0
  let totalUah = 0

  if (isMultiQtyProduct) {
    product.options[0].variants.forEach((variant) => {
      const qty = variantQuantities[variant.id] || 0
      if (qty > 0) {
        const vPriceUah = product.price_uah + Math.round(variant.price_usd_diff * variantRatio)
        totalQty += qty
        totalUah += vPriceUah * qty
      }
    })
  } else {
    totalUah = (total?.totalUah ?? product.price_uah) * quantity
  }

  const canAdd = isMultiQtyProduct ? totalQty > 0 : (product.in_stock || product.is_made_to_order)

  function selectVariant(optionId: number, variant: ProductOptionVariant) {
    haptic('light')
    setSelected((prev) => ({ ...prev, [optionId]: variant.id }))
  }

  function handleShare() {
    haptic('light')
    if (navigator.share) {
      void navigator.share({
        title: name,
        url: window.location.href,
      })
    } else if (navigator.clipboard) {
      void navigator.clipboard.writeText(window.location.href)
      alert(T.share)
    }
  }

  const primaryPhotoUrl = product.photos?.find(p => p.is_primary)?.url ?? product.photos?.[0]?.url ?? null
  const sortedPhotos = (product.photos || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  const currentPhoto = sortedPhotos[photoIdx]?.url ?? primaryPhotoUrl

  return (
    <div className={`min-h-screen pb-32 pt-4 ${
      isMayParfum ? 'bg-[#3a4352] text-[#f8f3ea]' : 'bg-[#15181c] text-slate-100'
    }`}>
      <div className="mx-auto max-w-3xl px-4">
        {/* TOP NAV BAR */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link
              to="/catalog"
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium backdrop-blur-sm transition-all ${
                isMayParfum
                  ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea] hover:border-[#657086]'
                  : 'border-slate-800 bg-slate-900/80 text-slate-200 hover:border-slate-700'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              {T.back}
            </Link>
            {homePageEnabled && (
              <Link
                to="/"
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium backdrop-blur-sm transition-all ${
                  isMayParfum
                    ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea] hover:border-[#657086]'
                    : 'border-slate-800 bg-slate-900/80 text-slate-200 hover:border-slate-700'
                }`}
              >
                <Home className="h-4 w-4" />
                {tNav.home}
              </Link>
            )}
          </div>
          <button
            onClick={handleShare}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium backdrop-blur-sm transition-all ${
              isMayParfum
                ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea] hover:border-[#657086]'
                : 'border-slate-800 bg-slate-900/80 text-slate-200 hover:border-slate-700'
            }`}
          >
            <Share2 className="h-3.5 w-3.5" />
            {T.share}
          </button>
        </div>

        {/* PHOTO CAROUSEL SECTION */}
        <section className={`overflow-hidden rounded-[28px] border shadow-lg transition-all ${
          isMayParfum
            ? 'border-[#444e5d] bg-[#28303c]'
            : 'border-slate-800 bg-[#1e2329]'
        }`}>
          <div className={`relative ${isMayParfum ? 'bg-[#1f242d]' : 'bg-[#15181c]'}`}>
            {currentPhoto ? (
              <img src={currentPhoto} alt={name} className="h-80 w-full object-cover sm:h-[28rem]" />
            ) : (
              <div className="flex h-80 items-center justify-center text-6xl sm:h-[28rem]">📦</div>
            )}
            {sortedPhotos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setPhotoIdx((prev) => (prev === 0 ? sortedPhotos.length - 1 : prev - 1))}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-slate-700 bg-slate-900/80 p-2 text-slate-200 shadow-md backdrop-blur-sm"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPhotoIdx((prev) => (prev === sortedPhotos.length - 1 ? 0 : prev + 1))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-slate-700 bg-slate-900/80 p-2 text-slate-200 shadow-md backdrop-blur-sm"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <div className="absolute bottom-3 right-3 rounded-full border border-slate-700 bg-slate-900/80 px-2.5 py-1 text-xs font-medium text-slate-200 backdrop-blur-sm">
                  {photoIdx + 1} / {sortedPhotos.length}
                </div>
              </>
            )}
          </div>

          {sortedPhotos.length > 1 && (
            <div className="flex gap-2 overflow-x-auto px-4 py-3">
              {sortedPhotos.map((photo, idx) => (
                <button
                  key={`${photo.url ?? 'empty'}-${idx}`}
                  type="button"
                  onClick={() => setPhotoIdx(idx)}
                  className={`overflow-hidden rounded-2xl border transition-all ${
                    idx === photoIdx
                      ? 'border-[#c9a24b] ring-2 ring-[#c9a24b]/30'
                      : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  {photo.url ? (
                    <img src={photo.url} alt={`${name} ${idx + 1}`} className="h-16 w-16 object-cover" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center bg-slate-800 text-xl">📦</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* DETAILS SECTION */}
        <section className={`mt-4 rounded-[28px] border p-5 shadow-lg transition-all ${
          isMayParfum
            ? 'border-[#444e5d] bg-[#28303c] text-[#f8f3ea]'
            : 'border-slate-800 bg-[#1e2329] text-slate-100'
        }`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              {brandName && brandSlug && (
                <Link
                  to="/brands/$brandSlug"
                  params={{ brandSlug }}
                  onClick={() => haptic('light')}
                  className="mb-2.5 inline-flex items-center gap-1.5 rounded-full border border-[#c9a24b]/40 bg-[#c9a24b]/10 px-3 py-1 text-xs font-semibold tracking-wider text-[#c9a24b] uppercase hover:bg-[#c9a24b]/20 hover:border-[#c9a24b]/70 transition shadow-2xs"
                >
                  <Sparkles className="h-3.5 w-3.5 text-[#c9a24b]" />
                  <span>{brandName}</span>
                  <ChevronRight className="h-3 w-3 text-[#c9a24b]/70" />
                </Link>
              )}
              <h1 className="text-2xl font-bold leading-tight tracking-wide">{name}</h1>
              {product.is_made_to_order && (
                <p className="mt-2 text-sm text-slate-400">
                  ⏳{' '}
                  {product.production_days
                    ? `${T.madeToOrder} • ${
                        product.production_days === 28
                          ? lang === 'en'
                            ? '~4 weeks'
                            : '~4 тижні'
                          : `~${product.production_days} ${daysLabel}`
                      }`
                    : T.madeToOrder}
                </p>
              )}
              {product.stock_qty !== null && (
                <p className="mt-1 text-sm text-slate-400">{stockLabel(lang, product.stock_qty)}</p>
              )}
              {!product.in_stock && !product.is_made_to_order && (
                <p className="mt-1 text-sm font-semibold text-rose-400">{T.outOfStock}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { haptic('light'); wishlistMutation.mutate() }}
              className={`shrink-0 rounded-full border p-3 transition-all ${
                wished
                  ? 'border-rose-500/50 bg-rose-500/10 text-rose-400'
                  : 'border-[#444e5d] bg-[#1f242d] text-slate-400 hover:text-slate-200'
              }`}
            >
              <Heart className={`h-5 w-5 ${wished ? 'fill-current' : ''}`} />
            </button>
          </div>

          {/* PRICE DISPLAY */}
          <div className="mt-4 flex items-end gap-2">
            <p className="text-3xl font-bold text-[#c9a24b]">
              {formatPrice(
                isMultiQtyProduct
                  ? totalQty > 0 ? totalUah : product.price_uah
                  : (total?.totalUah ?? product.price_uah)
              )}
            </p>
          </div>

          {/* VARIANT / VOLUME SELECTION */}
          {!product.is_kit && product.options.length > 0 && (
            <div className="mt-5 space-y-4">
              {isMultiQtyProduct ? (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {localizedName(lang, product.options[0].name_uk, product.options[0].name_en)} (Оберіть розпив або варіант)
                  </p>
                  
                  {/* COMPACT VOLUME CHIPS GRID */}
                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                    {product.options[0].variants.map((variant) => {
                      const variantName = localizedName(lang, variant.name_uk, variant.name_en)
                      const qty = variantQuantities[variant.id] || 0
                      const active = qty > 0

                      const variantPriceUah = product.price_uah + Math.round(variant.price_usd_diff * variantRatio)

                      const volMatch = variantName.match(/(\d+(?:[.,]\d+)?)\s*(?:мл|ml)/i)
                      const sprays = volMatch ? Math.round(parseFloat(volMatch[1].replace(',', '.')) * 15) : null

                      return (
                        <div
                          key={variant.id}
                          className={`relative flex flex-col justify-between rounded-2xl border p-3 transition-all ${
                            active
                              ? 'border-[#c9a24b] bg-[#c9a24b]/15 shadow-md shadow-[#c9a24b]/5 ring-1 ring-[#c9a24b]/40'
                              : 'border-[#444e5d] bg-[#1f242d] hover:border-slate-500'
                          }`}
                        >
                          <div>
                            <div className="flex items-center justify-between gap-1">
                              <span className={`text-sm font-bold leading-tight ${active ? 'text-[#c9a24b]' : 'text-slate-100'}`}>
                                {variantName}
                              </span>
                              {sprays && (
                                <span className="text-[10px] font-medium text-slate-400">
                                  ≈{sprays} розп.
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs font-semibold text-[#c9a24b]">
                              {formatPrice(variantPriceUah)}
                            </p>
                          </div>

                          {/* STEPPER INSIDE CHIP */}
                          <div className="mt-2.5 border-t border-[#444e5d]/40 pt-2">
                            {active ? (
                              <div className="flex w-full items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => {
                                    haptic('light')
                                    setVariantQuantities((prev) => ({
                                      ...prev,
                                      [variant.id]: Math.max(0, (prev[variant.id] || 0) - 1),
                                    }))
                                  }}
                                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#c9a24b]/20 text-[#f8f3ea] hover:bg-[#c9a24b]/30 transition"
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span className="text-xs font-bold text-[#c9a24b]">{qty}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    haptic('light')
                                    setVariantQuantities((prev) => ({
                                      ...prev,
                                      [variant.id]: (prev[variant.id] || 0) + 1,
                                    }))
                                  }}
                                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#c9a24b] text-[#17120f] hover:bg-[#d4af37] transition"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  haptic('light')
                                  setVariantQuantities((prev) => ({ ...prev, [variant.id]: 1 }))
                                }}
                                className="flex w-full items-center justify-center gap-1 rounded-lg bg-[#2a313d] py-1.5 text-xs font-semibold text-slate-200 hover:bg-[#c9a24b] hover:text-[#17120f] transition"
                              >
                                <Plus className="h-3 w-3" />
                                Обрати
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                product.options.map((option, optionIdx) => {
                  const optionName = localizedName(lang, option.name_uk, option.name_en)
                  return (
                    <div key={option.id}>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {optionName}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {option.variants.map((variant) => {
                          const variantName = localizedName(lang, variant.name_uk, variant.name_en)
                          const active = selected[option.id] === variant.id
                          const isVolumeOrFirst = optionIdx === 0 || optionName.toLowerCase().includes('об') || optionName.toLowerCase().includes('vol')

                          const variantPriceUah = product.price_uah + Math.round(variant.price_usd_diff * variantRatio)
                          const diffUah = Math.round(variant.price_usd_diff * variantRatio)

                          const volMatch = variantName.match(/(\d+(?:[.,]\d+)?)\s*(?:мл|ml)/i)
                          const sprays = volMatch ? Math.round(parseFloat(volMatch[1].replace(',', '.')) * 15) : null

                          return (
                            <button
                              key={variant.id}
                              type="button"
                              onClick={() => selectVariant(option.id, variant)}
                              className={`rounded-2xl border px-3.5 py-2 text-left transition-all flex flex-col justify-center min-w-[110px] ${
                                active
                                  ? 'border-[#c9a24b] bg-[#c9a24b] text-[#17120f] font-semibold shadow-md shadow-[#c9a24b]/10'
                                  : 'border-[#444e5d] bg-[#1f242d] text-slate-200 hover:border-slate-500'
                              }`}
                            >
                              <span className="font-semibold text-sm leading-tight">{variantName}</span>
                              <div className="flex flex-wrap items-center gap-1 text-[10px] leading-none mt-1">
                                <span className={active ? 'text-[#17120f]/80 font-bold' : 'text-[#c9a24b]'}>
                                  {isVolumeOrFirst
                                    ? formatPrice(variantPriceUah)
                                    : diffUah !== 0
                                      ? diffUah > 0 ? `+${formatPrice(diffUah)}` : `-${formatPrice(Math.abs(diffUah))}`
                                      : T.free
                                  }
                                </span>
                                {sprays && (
                                  <span className={active ? 'text-[#17120f]/60' : 'text-slate-400'}>
                                    • {T.spraysEstimate ? T.spraysEstimate(sprays) : (lang === 'en' ? `≈ ${sprays} sprays` : lang === 'ru' ? `≈ ${sprays} расп.` : `≈ ${sprays} розп.`)}
                                  </span>
                                )}
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          )}

          {/* DESCRIPTION & SPECS TABS */}
          {(description || hasAttributes) && (
            <div className="mt-6 border-t border-[#444e5d]/60 pt-4">
              <div className="mb-3 grid grid-cols-2 rounded-2xl bg-[#1f242d] p-1 text-sm font-semibold border border-[#444e5d]/40">
                <button
                  type="button"
                  onClick={() => setInfoTab('description')}
                  disabled={!description}
                  className={`rounded-xl px-3 py-2 transition-all ${
                    activeInfoTab === 'description'
                      ? 'bg-[#c9a24b] text-[#17120f] shadow-sm'
                      : description
                        ? 'text-slate-400 hover:text-slate-200'
                        : 'cursor-not-allowed text-slate-600'
                  }`}
                >
                  {T.description}
                </button>
                <button
                  type="button"
                  onClick={() => setInfoTab('attributes')}
                  disabled={!hasAttributes}
                  className={`rounded-xl px-3 py-2 transition-all ${
                    activeInfoTab === 'attributes'
                      ? 'bg-[#c9a24b] text-[#17120f] shadow-sm'
                      : hasAttributes
                        ? 'text-slate-400 hover:text-slate-200'
                        : 'cursor-not-allowed text-slate-600'
                  }`}
                >
                  {T.attributes}
                </button>
              </div>

              {activeInfoTab === 'description' && (
                <Markdown
                  content={description}
                  className="text-sm leading-6 space-y-3 text-slate-300"
                />
              )}

              {activeInfoTab === 'attributes' && (
                <dl className="divide-y divide-[#444e5d]/40 overflow-hidden rounded-2xl border border-[#444e5d]/60 bg-[#1f242d]">
                  {specs.map((spec) => {
                    const specName = localizedName(lang, spec.name_uk, spec.name_en)
                    const isBrandSpec = spec.code === 'brand' || spec.name_uk?.toLowerCase() === 'бренд' || spec.name_en?.toLowerCase() === 'brand'
                    const ukValues = spec.value_uk.split(',').map((v) => v.trim()).filter(Boolean)
                    const enValues = spec.value_en ? spec.value_en.split(',').map((v) => v.trim()).filter(Boolean) : []
                    return (
                      <div key={`${spec.name_uk}:${spec.value_uk}`} className="grid grid-cols-[0.9fr_1.1fr] gap-3 px-3.5 py-3">
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          {specName}
                        </dt>
                        <dd className="min-w-0">
                          <div className="flex flex-wrap gap-1.5">
                            {ukValues.map((valUk, i) => {
                              const valEn = enValues[i] || valUk
                              const valName = lang === 'en' ? valEn : valUk
                              if (isBrandSpec) {
                                const valSlug = slugify(valUk)
                                return (
                                  <Link
                                    key={valUk}
                                    to="/brands/$brandSlug"
                                    params={{ brandSlug: valSlug }}
                                    onClick={() => haptic('light')}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a24b]/50 bg-[#c9a24b]/10 px-2.5 py-1 text-xs font-semibold text-[#c9a24b] hover:bg-[#c9a24b]/20 transition"
                                  >
                                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-[#c9a24b]" />
                                    <span>{valName}</span>
                                    <ChevronRight className="h-3 w-3" />
                                  </Link>
                                )
                              }
                              const sCode = spec.code || slugify(spec.name_uk || 'spec')
                              const valSlug = slugify(valUk)
                              return (
                                <Link
                                  key={valUk}
                                  to="/specs/$specCode/$valueSlug"
                                  params={{ specCode: sCode, valueSlug: valSlug }}
                                  onClick={() => haptic('light')}
                                  className="inline-flex items-center gap-1.5 rounded-full border border-[#444e5d] bg-[#28303c] px-2.5 py-1 text-xs font-semibold text-slate-200 hover:border-[#c9a24b] hover:text-[#c9a24b] transition"
                                >
                                  <Tags className="h-3.5 w-3.5 shrink-0 text-[#c9a24b]" />
                                  <span>{valName}</span>
                                  <ChevronRight className="h-3 w-3 opacity-60" />
                                </Link>
                              )
                            })}

                          </div>
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              )}
            </div>
          )}

          {/* KIT CONTENTS */}
          {product.is_kit && product.kit_items && product.kit_items.length > 0 && (
            <div className="mt-6 border-t border-[#444e5d]/60 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-200">
                {T.kitContents}
              </h3>
              <ul className="space-y-2.5">
                {product.kit_items.map((item, idx) => {
                  const subName = localizedName(lang, item.name_uk, item.name_en)
                  return (
                    <li key={idx} className="flex items-center gap-3 text-sm text-slate-300">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#c9a24b]/20 text-[10px] font-bold text-[#c9a24b]">
                        {item.quantity}×
                      </span>
                      <Link
                        to="/catalog/$productSlug"
                        params={{ productSlug: String(item.sub_product_id) }}
                        className="font-medium text-slate-100 hover:text-[#c9a24b] hover:underline transition"
                      >
                        {subName}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* EXTRA ACTIONS */}
          <div className="mt-6 flex flex-wrap gap-2">
            {product.youtube_link && (
              <a
                href={product.youtube_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl border border-[#444e5d] bg-[#1f242d] px-4 py-2.5 text-sm font-medium text-slate-200 hover:border-red-500/50 hover:text-red-400 transition"
              >
                <PlayCircle className="h-4 w-4 text-red-500" />
                {T.watchVideo}
              </a>
            )}
            <button
              type="button"
              onClick={() => { haptic('light'); wishlistMutation.mutate() }}
              className="inline-flex items-center gap-2 rounded-2xl border border-[#444e5d] bg-[#1f242d] px-4 py-2.5 text-sm font-medium text-slate-200 hover:border-rose-500/50 hover:text-rose-400 transition"
            >
              <Heart className={`h-4 w-4 ${wished ? 'fill-current text-rose-500' : 'text-slate-400'}`} />
              {wished ? T.inWishlist : T.addToWishlist}
            </button>
          </div>

          {/* DELIVERY, PAYMENT & FOP REQUISITES BLOCK */}
          <div className={`mt-6 rounded-2xl border p-4 space-y-3.5 transition-all ${
            isMayParfum
              ? 'border-[#444e5d]/60 bg-[#1f242d] text-slate-200'
              : 'border-slate-800 bg-[#15181c] text-slate-200'
          }`}>
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#c9a24b]/15 text-[#c9a24b]">
                <Truck className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-xs font-bold text-slate-100">
                  {T.fastDeliveryTitle}
                </h4>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  {T.fastDeliveryText}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 border-t border-[#444e5d]/40 pt-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#c9a24b]/15 text-[#c9a24b]">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h4 className="text-xs font-bold text-slate-100">
                    {T.officialFopTitle}
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      haptic('light')
                      setShowFopModal(true)
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#c9a24b] hover:text-[#d8b25c] hover:underline transition"
                  >
                    <span>{tCheckout.viewFopRequisites}</span>
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  {T.officialFopText}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 border-t border-[#444e5d]/40 pt-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#c9a24b]/15 text-[#c9a24b]">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-xs font-bold text-slate-100">
                  {lang === 'en' ? '100% Original Perfumery' : '100% Оригінальна парфумерія'}
                </h4>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  {lang === 'en'
                    ? 'Authentic fragrances from verified European distributors with checkable batch codes.'
                    : 'Тільки оригінал від перевірених постачальників, свіжі батч-коди та гарантія якості.'}
                </p>
              </div>
            </div>
          </div>

          <ReviewsSection
            productId={product.id}
            ratingAvg={product.rating_avg}
            ratingCount={product.rating_count}
            canReview={product.can_review}
            reviews={product.reviews}
            isMayParfum={isMayParfum}
          />
        </section>

        {isMayParfum && (
          <ScentEvolutionPlayer
            productId={product.id}
            specs={product.specs}
            isMayParfum={isMayParfum}
          />
        )}

        {isMayParfum && (
          <SimilarFragrances productId={product.id} isMayParfum={isMayParfum} />
        )}

        <RelatedProducts productId={product.id} isMayParfum={isMayParfum} />

        <FopRequisitesModal
          isOpen={showFopModal}
          onClose={() => setShowFopModal(false)}
          amountUah={total?.totalUah ?? product.price_uah}
        />
      </div>

      {/* BOTTOM FIXED ACTION BAR */}
      <div className={`app-bottom-fixed app-bottom-panel border-t p-3.5 flex flex-col gap-2 backdrop-blur-md ${
        isMayParfum
          ? 'bg-[#28303c]/95 border-[#444e5d]'
          : 'bg-[#1e2329]/95 border-slate-800'
      }`}>
        <div className="flex items-center gap-2.5 w-full">
          {!isMultiQtyProduct && (
            <div className="flex items-center gap-1 rounded-2xl border border-[#444e5d] bg-[#1f242d] p-1 text-slate-100">
              <button
                type="button"
                aria-label="Decrease quantity"
                onClick={() => { haptic('light'); setQuantity(q => Math.max(1, q - 1)) }}
                className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-[#444e5d]/40 text-slate-200 transition"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-8 text-center text-sm font-bold text-[#c9a24b]">{quantity}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => { haptic('light'); setQuantity(q => q + 1) }}
                className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-[#444e5d]/40 text-slate-200 transition"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}

          <button
            type="button"
            disabled={!canAdd || addMutation.isPending}
            onClick={() => { haptic('medium'); addMutation.mutate() }}
            className={`flex-1 flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-bold shadow-lg transition-all ${
              canAdd
                ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d4af37] active:scale-[0.99]'
                : 'cursor-not-allowed bg-slate-800 text-slate-500'
            }`}
          >
            {isMultiQtyProduct && totalQty === 0 ? (
              lang === 'en' ? 'Select volume above' : 'Оберіть об\'єм вище'
            ) : !canAdd ? (
              T.outOfStock
            ) : addMutation.isPending ? (
              T.adding
            ) : (
              <>
                <ShoppingCart className="h-4 w-4" />
                <span>
                  {T.addToCart} • {formatPrice(totalUah)}
                </span>
              </>
            )}
          </button>
        </div>
        <button
          type="button"
          onClick={() => void navigate({ to: '/cart' })}
          className="text-center text-xs font-medium text-slate-400 hover:text-slate-200 transition"
        >
          {T.added}
        </button>
      </div>
    </div>
  )
}
